<template>
    <BaseSidebar
        ref="accountSidebar"
        :is-open="showAccountSidebar"
        :on-close="() => setAccountSidebar(false)"
        :has-invitation-banner="hasInvitationBanner"
    >
        <template v-if="selectedCard" #header-left>
            <div class="flex items-center">
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:bg-slate-700"
                    :class="{
                        'opacity-50 cursor-not-allowed': isFirstCard,
                    }"
                    aria-label="Up arrow"
                    id="hs-sidebar-arrow-up"
                    @click="navigateToPreviousCard"
                    :disabled="isFirstCard"
                >
                    <Icon icon="chevron-up" :width="20" :height="20" />
                </button>
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:bg-slate-700 ml-2"
                    :class="{
                        'opacity-50 cursor-not-allowed': isLastCard,
                    }"
                    aria-label="Down arrow"
                    id="hs-sidebar-arrow-down"
                    @click="navigateToNextCard"
                    :disabled="isLastCard"
                >
                    <Icon icon="chevron-down" :width="20" :height="20" />
                </button>
                <div
                    class="flex pl-2 gap-2 justify-start items-start text-slate-400 text-sm font-normal leading-5"
                >
                    {{ currentCardIndex }} of {{ totalCards }} cards
                </div>
            </div>
        </template>
        <div v-if="selectedCard" class="flex justify-center mb-4 mt-4">
            <span class="block text-center w-full">
                <div
                    class="relative mx-auto inline-block w-full max-w-[90vw] lg:w-[352px] lg:h-[216px]"
                >
                    <ImageLoader v-if="selectedCard?.card">
                        <template #image>
                            <img
                                :src="`${cdnUrl}/${selectedCard.card.card_image?.path}/_small/${selectedCard.card.card_image?.file_name}`"
                                class="w-full h-auto lg:h-[216px] object-contain rounded-lg"
                            />
                        </template>
                        <template #error>
                            <div
                                class="flex flex-col items-center justify-center w-full h-[216px] lg:w-[352px] lg:h-[216px] bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-300 rounded-lg shadow-sm overflow-hidden relative"
                            >
                                <div
                                    class="absolute top-0 left-0 w-16 h-10 bg-slate-300 m-4 rounded-md"
                                ></div>
                                <div
                                    class="absolute bottom-6 left-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                                ></div>
                                <div
                                    class="absolute bottom-6 right-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                                ></div>

                                <div
                                    class="flex flex-col items-center justify-center p-4 text-center w-full"
                                >
                                    <div
                                        class="text-slate-700 dark:text-slate-200 font-medium text-xl mb-2 w-full max-w-[90%] truncate"
                                    >
                                        {{
                                            selectedCard.unmatched
                                                ? 'Unmatched Card'
                                                : selectedCard.card?.title ||
                                                  'Card'
                                        }}
                                    </div>
                                    <div class="text-slate-500 dark:text-slate-400 text-sm">
                                        {{
                                            selectedCard.institution?.name ||
                                            'Bank Name'
                                        }}
                                    </div>
                                </div>

                                <div class="absolute bottom-14 left-4 right-4">
                                    <div
                                        class="flex justify-between items-center"
                                    >
                                        <div class="text-slate-600 dark:text-slate-300 text-xs">
                                            {{
                                                selectedCard.last_four
                                                    ? '••••  ••••  ••••  ' +
                                                      selectedCard.last_four
                                                    : '••••  ••••  ••••  ••••'
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #preloader>
                            <div
                                class="w-full lg:w-[352px] lg:h-[216px] rounded-lg overflow-hidden shrink-0 shimmer"
                            ></div>
                        </template>
                    </ImageLoader>
                    <div
                        v-else
                        class="flex flex-col items-center justify-center w-full h-[216px] lg:w-[352px] lg:h-[216px] bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-300 rounded-lg shadow-sm overflow-hidden relative"
                    >
                        <div
                            class="absolute top-0 left-0 w-16 h-10 bg-slate-300 m-4 rounded-md"
                        ></div>
                        <div
                            class="absolute bottom-6 left-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                        ></div>
                        <div
                            class="absolute bottom-6 right-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                        ></div>

                        <div
                            class="flex flex-col items-center justify-center p-4 text-center w-full"
                        >
                            <div
                                class="text-slate-700 dark:text-slate-200 font-medium text-xl mb-2 w-full max-w-[90%] truncate"
                            >
                                {{
                                    selectedCard?.unmatched
                                        ? 'Unmatched Card'
                                        : selectedCard?.card?.title || 'Card'
                                }}
                            </div>
                            <div class="text-slate-500 dark:text-slate-400 text-sm">
                                {{
                                    selectedCard?.institution?.name ||
                                    'Bank Name'
                                }}
                            </div>
                        </div>

                        <div class="absolute bottom-14 left-4 right-4">
                            <div class="flex justify-between items-center">
                                <div class="text-slate-600 dark:text-slate-300 text-xs">
                                    {{
                                        selectedCard?.last_four
                                            ? '••••  ••••  ••••  ' +
                                              selectedCard?.last_four
                                            : '••••  ••••  ••••  ••••'
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Hidden badge overlay -->
                    <Transition name="fade">
                        <div
                            v-if="isHiddenCard"
                            class="absolute bottom-2 right-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                            :class="hiddenBadgeClasses"
                        >
                            <Icon icon="eye-off" class="w-3 h-3" />
                            <span class="text-xs font-medium leading-4">
                                Hidden in wallet
                            </span>
                        </div>
                    </Transition>
                </div>
            </span>
        </div>
        <div
            v-if="selectedCard"
            class="flex flex-col items-center justify-center w-full"
        >
            <div class="text-center">
                <div class="inline-block text-center w-full max-w-[90%]">
                    <span
                        v-if="selectedCard?.card"
                        class="text-xl font-medium text-slate-900 dark:text-slate-100"
                    >
                        {{
                            selectedCard.unmatched
                                ? 'Unmatched Card'
                                : selectedCard.card.title
                        }}
                    </span>
                    <span
                        v-if="selectedCard?.last_four"
                        class="text-xl font-normal text-slate-400 dark:text-slate-500 font-sans ml-1"
                    >
                        ({{ selectedCard.last_four }})
                    </span>
                </div>
            </div>
            <component
                :is="connectionProblem ? 'a' : 'div'"
                v-if="selectedCard?.guid && selectedCard?.member_guid"
                :href="connectionProblem ? '/wallet/members' : undefined"
                class="mt-2 flex items-center gap-2 rounded-full"
                :class="{ 'cursor-pointer hover:font-bold': connectionProblem }"
            >
                <span>
                    <Icon
                        :icon="
                            connectionProblem ? 'bank-error' : 'bank-financial'
                        "
                        :width="17"
                        :height="16"
                        :class="{
                            'text-slate-500': !connectionProblem,
                            'text-red-500': connectionProblem,
                        }"
                    />
                </span>
                <span
                    class="text-sm font-normal leading-5"
                    :class="{
                        'text-slate-400': !connectionProblem,
                        'text-red-500': connectionProblem,
                    }"
                >
                    Connected via bank{{
                        connectionProblem ? ': Error, click to see details' : ''
                    }}
                </span>
            </component>
            <div
                class="flex justify-center items-center gap-2 pt-4 w-[368px]"
                v-if="selectedCard?.card"
            >
                <button
                    class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                    type="button"
                    @click.prevent="openEditCardModal"
                    id="hs-wallet-edit-card"
                >
                    <span>
                        <Icon
                            icon="pencil"
                            :width="20"
                            :height="20"
                            class="text-slate-800 dark:text-slate-100 scale-125"
                        />
                    </span>
                    <span class="text-slate-900 dark:text-slate-100 text-sm font-medium leading-5">
                        Edit
                    </span>
                </button>
                <div class="relative" ref="moreMenuContainer">
                    <button
                        class="flex items-center justify-center w-9 h-9 p-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                        type="button"
                        id="hs-wallet-extra-action"
                        @click="toggleMoreMenu"
                    >
                        <Icon icon="dots-vertical" :width="20" :height="20" />
                    </button>
                    <!-- Popover Menu -->
                    <div
                        v-if="showMoreMenu"
                        class="absolute right-0 mt-2 flex p-2 flex-col items-start border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-sm z-50 min-w-[180px]"
                        @click.stop
                    >
                        <!-- Copy card details -->
                        <button
                            @click="copyCardDetails"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                        >
                            <Icon icon="copy" :width="20" :height="20" />
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                Copy card details
                            </span>
                        </button>

                        <!-- Welcome bonus quick actions (MY1-3043) -->
                        <button
                            v-if="bonusMenuMarkVisible"
                            @click="menuMarkBonusReceived"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                        >
                            <Icon icon="check-lg" :width="20" :height="20" />
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5 text-left"
                            >
                                Goal met, mark bonus as received
                            </span>
                        </button>
                        <button
                            v-if="bonusMenuUnmarkVisible"
                            @click="menuUnmarkBonusReceived"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                        >
                            <Icon icon="circle-x" :width="20" :height="20" />
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5 text-left"
                            >
                                Remove "Marked as received"
                            </span>
                        </button>

                        <!-- Toggle visibility -->
                        <button
                            @click="toggleCardVisibility"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                        >
                            <Icon
                                :icon="isHiddenCard ? 'eye-on' : 'eye-off'"
                                :width="20"
                                :height="20"
                            />
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                {{
                                    isHiddenCard
                                        ? 'Show in wallet'
                                        : 'Hide in wallet'
                                }}
                            </span>
                        </button>

                        <!-- Delete card -->
                        <button
                            v-if="
                                !(
                                    selectedCard.plaid_account_id ||
                                    selectedCard.connection_source ||
                                    selectedCard.is_connected ||
                                    selectedCard.source === 'connect_to_bank'
                                )
                            "
                            @click="showDeleteConfirmation = true"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 text-red-500 transition-colors duration-150"
                        >
                            <Icon icon="delete" :width="20" :height="20" />
                            <span class="text-sm font-normal leading-5">
                                Delete
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 relative" v-else>
                <button
                    class="btn-base btn-white"
                    @click.stop="handleMatchCard()"
                >
                    Match
                </button>
                <div
                    v-if="
                        selectedCard?.card_match_request &&
                        selectedCard?.card_match_request?.rejected
                    "
                    class="absolute -bottom-1.5 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-red-500 text-white shadow-lg"
                >
                    <Icon icon="alert" class="w-2.5 h-2.5" />
                </div>
            </div>
        </div>
        <CardDetails
            v-if="selectedCard"
            ref="cardDetails"
            :user="user"
            @add-product-change="handleAddProductChange"
            @edit-product-change="handleEditProductChange"
            @delete-product-change="handleDeleteProductChange"
        />
    </BaseSidebar>

    <!-- Delete Confirmation Modal -->
    <ConfirmationDialog
        :show="showDeleteConfirmation"
        :dialog="`Are you sure you want to delete this card? This also deletes its benefit usage history, including credits logged on it, and its benefit settings. This action cannot be undone.`"
        icon="trash"
        iconColor="red"
        @cancel="showDeleteConfirmation = false"
        @confirm="handleDeleteCard(selectedCard)"
        :buttons="[
            {
                type: 'cancel',
                text: 'Cancel',
            },
            {
                type: 'confirm',
                text: 'Delete',
                value: true,
                classes: '!bg-red-500 text-white',
            },
        ]"
    >
        <template #title>
            <div class="flex items-center gap-3 self-stretch">
                <div
                    class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                >
                    <Icon icon="trash" :pathClass="'fill-red-500'" />
                </div>
                <span
                    class="text-slate-900 dark:text-slate-100 font-inter text-xl font-medium leading-7"
                >
                    Delete {{ selectedCard?.card?.title
                    }}{{
                        !selectedCard?.card?.title
                            ?.toLowerCase()
                            .includes('card')
                            ? ' Card'
                            : ''
                    }}?
                </span>
            </div>
        </template>
    </ConfirmationDialog>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import Icon from '@/components/ui/icon/icon.vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import CardDetails from '@/components/wallet/partials/card-details.vue';

export default {
    props: {
        user: { type: Object },
    },
    components: {
        Icon,
        ConfirmationDialog,
        ImageLoader,
        BaseSidebar,
        CardDetails,
    },
    data() {
        return {
            showDeleteConfirmation: false,
            showMoreMenu: false,
        };
    },

    computed: {
        hasInvitationBanner() {
            return document.body.classList.contains('has-invitation');
        },

        ...mapState(useWalletStore, [
            'selectedCard',
            'showAccountSidebar',
            'wallet',
            'walletId',
            'walletCards',
            'cdnUrl',
            'cdnThumbnailUrl',
            'apiUrl',
            'rewards',
            'walletCards',
            'features',
            'visibleCards',
            'groupedCards',
            'groupBy',
            'subgroupBy',
            'groupSameCards',
        ]),

        hiddenBadgeClasses() {
            const isLightBackground =
                this.selectedCard?.card?.is_light_background || false;
            return this.getBadgeClasses(isLightBackground);
        },

        // MY1-3043 — welcome-bonus quick actions in the header menu. Mark is
        // offered while anything is still unreceived; un-mark while anything
        // is received (tiered bonuses target the active / last-received tier).
        bonusMenuMarkVisible() {
            const bonus = this.selectedCard?.welcome_bonus;
            if (!bonus || this.selectedCard?.product_changed) {
                return false;
            }
            if (bonus.tiers?.length) {
                return bonus.tiers.some((tier) => !tier.received);
            }
            return !bonus.received && !bonus.goal_not_met;
        },

        bonusMenuUnmarkVisible() {
            const bonus = this.selectedCard?.welcome_bonus;
            if (!bonus || this.selectedCard?.product_changed) {
                return false;
            }
            if (bonus.tiers?.length) {
                return bonus.tiers.some((tier) => tier.received);
            }
            return Boolean(bonus.received);
        },

        isFirstCard() {
            return this.currentCardIndex <= 1;
        },

        isLastCard() {
            return this.currentCardIndex >= this.totalCards;
        },
        currentCardIndex() {
            if (!this.visibleCards || !this.selectedCard) return 0;
            const idx = this.flattenedCards.findIndex(
                (card) => card.id === this.selectedCard.id
            );
            return idx >= 0 ? idx + 1 : 0;
        },
        flattenedCards() {
            // standardizes the cards for navigation that matches the order in the wallet table
            return this.recursivelyFlattenCards(
                this.visibleCards,
                this.groupedCards
            );
        },
        totalCards() {
            if (!this.visibleCards) return 0;
            return this.flattenedCards.length;
        },
        cdnUrl() {
            return window.cdnUrl;
        },
        isHiddenCard() {
            return this.selectedCard?.ignore;
        },
        connectionProblem() {
            return this.getConnectionProblemForCard(this.selectedCard);
        },
    },

    watch: {
        selectedCard: {
            immediate: true,
            handler(newCard) {
                this.showMoreMenu = false;
                this.scrollToTop();
            },
        },
    },

    mounted() {
        document.addEventListener('click', this.closeMoreMenuOnClickOutside);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.closeMoreMenuOnClickOutside);
    },

    methods: {
        ...mapActions(useWalletStore, [
            'deleteCard',
            'setAccountSidebar',
            'setSelectedCard',
            'setSelectedUser',
            'setShowCopyCardModal',
            'updateCardVisibility',
            'setShowEditCardModal',
            'setShowProductChangeModal',
            'setEditingProductChange',
            'openCardMatchModal',
            'getWalletByEntities',
            'getConnectionProblemForCard',
            'showNotification',
        ]),

        // MY1-3043 — delegate to card-details, which owns the modal + API.
        menuMarkBonusReceived() {
            this.showMoreMenu = false;
            this.$refs.cardDetails?.openMarkReceivedFromMenu();
        },

        menuUnmarkBonusReceived() {
            this.showMoreMenu = false;
            this.$refs.cardDetails?.unmarkReceived();
        },

        toggleMoreMenu() {
            this.showMoreMenu = !this.showMoreMenu;
        },

        toggleCardVisibility() {
            if (!this.selectedCard) return;

            const hidden = !this.selectedCard.ignore;

            this.updateCardVisibility(this.selectedCard.id, hidden)
                .then((response) => {
                    if (response.data.success) {
                        this.setSelectedCard({
                            ...this.selectedCard,
                            ignore: response.data.ignore,
                        });
                    }
                })
                .catch((error) => {
                    console.error('Failed to update card visibility:', error);
                });
        },

        copyCardDetails() {
            this.setShowCopyCardModal(true);
            this.setAccountSidebar(false);
        },

        openEditCardModal() {
            this.setSelectedUser(this.user);
            this.setShowEditCardModal(true);
        },

        getBadgeClasses(isLightBackground) {
            return isLightBackground
                ? 'bg-slate-800 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100';
        },

        closeMoreMenuOnClickOutside(event) {
            if (
                this.$refs.moreMenuContainer &&
                !this.$refs.moreMenuContainer.contains(event.target)
            ) {
                this.showMoreMenu = false;
            }
        },

        scrollToTop() {
            if (this.$refs.accountSidebar) {
                this.$refs.accountSidebar.scrollToTop();
            }
        },

        navigateToPreviousCard() {
            if (this.isFirstCard) return;

            if (this.currentCardIndex > 0) {
                this.setSelectedCard(
                    this.flattenedCards[this.currentCardIndex - 2]
                );
            }
        },

        navigateToNextCard() {
            if (this.isLastCard) return;
            if (this.currentCardIndex < this.flattenedCards.length) {
                this.setSelectedCard(
                    this.flattenedCards[this.currentCardIndex]
                );
            }
        },

        // Confirm before deleting card
        confirmDeleteCard() {
            this.showDeleteConfirmation = true;
        },

        // Delete card from wallet
        handleDeleteCard(card) {
            this.showMoreMenu = false;

            this.setAccountSidebar(false);

            this.showDeleteConfirmation = false;
            this.deleteCard(card);
        },

        // Handle adding a new product change
        handleAddProductChange(card) {
            // Open the edit card modal to add a new product change
            this.setSelectedUser(this.user);
            this.setShowEditCardModal(true);
            this.setShowProductChangeModal(true);
        },

        // Handle editing an existing product change
        handleEditProductChange(card, productChange) {
            this.setSelectedUser(this.user);
            this.setEditingProductChange(productChange);
            this.setShowEditCardModal(true);
        },

        // Handle deleting a product change
        async handleDeleteProductChange(card, productChangeId) {
            try {
                await axios.delete(
                    `/api/wallets/cards/${card.id}/product-changes/${productChangeId}`
                );
                // Refresh wallet data - use getWalletByEntities which actually loads the cards.
                // Background mode: keep the table visible with only the subtle indicator.
                await this.getWalletByEntities(null, { background: true });

                // Update selectedCard with fresh data to trigger Vue reactivity
                if (this.selectedCard) {
                    const updatedCard = this.walletCards.find(
                        (c) => c.id === this.selectedCard.id
                    );
                    if (updatedCard) {
                        this.setSelectedCard(updatedCard);
                    } else {
                        // Card no longer exists in wallet
                        this.setAccountSidebar(false);
                    }
                }

                this.showNotification(
                    'Product Change Deleted',
                    'Your product change has been successfully deleted.'
                );
            } catch (error) {
                console.error('Error deleting product change:', error);
                this.showNotification(
                    'Error',
                    'Failed to delete product change',
                    10000,
                    'error'
                );
            }
        },

        recursivelyFlattenCards(visibleCards, groupedCards) {
            const result = [];

            if (!this.groupBy && visibleCards) {
                return this.extractCardsFromItems(visibleCards);
            }

            if (groupedCards && typeof groupedCards === 'object') {
                if (this.subgroupBy) {
                    Object.values(groupedCards).forEach((subgroupsObj) => {
                        if (subgroupsObj && typeof subgroupsObj === 'object') {
                            Object.values(subgroupsObj).forEach(
                                (subgroupCards) => {
                                    if (Array.isArray(subgroupCards)) {
                                        result.push(
                                            ...this.extractCardsFromItems(
                                                subgroupCards
                                            )
                                        );
                                    }
                                }
                            );
                        }
                    });
                } else {
                    Object.values(groupedCards).forEach((groupCards) => {
                        if (Array.isArray(groupCards)) {
                            result.push(
                                ...this.extractCardsFromItems(groupCards)
                            );
                        }
                    });
                }
            }

            return result;
        },
        handleMatchCard() {
            this.openCardMatchModal(this.selectedCard);
            this.setAccountSidebar(false);
        },
        extractCardsFromItems(items) {
            if (!Array.isArray(items)) return [];

            const result = [];

            items.forEach((item) => {
                if (
                    this.groupSameCards &&
                    item &&
                    item.cards &&
                    Array.isArray(item.cards)
                ) {
                    result.push(...item.cards);
                } else if (item) {
                    result.push(item);
                }
            });

            return result;
        },
    },
};
</script>
<style lang="scss" scoped>
.sidebar-overlay {
    background: transparent;
    position: fixed;
    inset: 0;
    z-index: 140;
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
    transition:
        transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
    transform: translateX(100%);
    opacity: 0;
}
.sidebar-slide-enter-to,
.sidebar-slide-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.account-sidebar {
    will-change: transform, opacity;
}

.fade-bottom {
    mask-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 1) 40%,
        rgba(0, 0, 0, 0) 90%
    );
    -webkit-mask-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 1) 40%,
        rgba(0, 0, 0, 0) 90%
    );
    border-bottom-color: transparent !important;
    max-height: 80px;
    overflow: hidden;
}
</style>
